import {
  Component,
  OnInit,
  ViewChild,
  ViewChildren,
  QueryList,
} from "@angular/core";
import { UntypedFormGroup, UntypedFormControl, Validators } from "@angular/forms";
import { DateValidation } from "../../validators/dateValidation";
import { AgGridAngular } from "ag-grid-angular";
import { localeSwedish } from "../locale.swedish";
import { ToastrService } from "ngx-toastr";
import * as moment from "moment";
import { activityAbbreviations } from "../../common/enums";
import * as XLSX from "xlsx";
import {
  SPINNER_ANIMATIONS,
  SPINNER_PLACEMENT,
  ISpinnerConfig,
  NgxSpinner,
} from "@hardpool/ngx-spinner";
import {
  Driver,
  FlightData,
  FlightReport,
} from "../../model/report/flightReport";
import { ExcelReportWriterHelper } from "../../helpers/excelReportWriterHelper";
import { ReportService } from "../../services/report.service";

@Component({
  selector: "flight",
  templateUrl: "flight.component.html",
  styleUrls: ["flight.component.css"],
})
export class FlightComponent implements OnInit {
  @ViewChildren("activities") activityGrids: QueryList<AgGridAngular>;
  @ViewChild("spinner", { static: true }) spinner: NgxSpinner;

  searchForm: UntypedFormGroup;
  busy: boolean = false;
  noMatch: boolean = false;
  canExportToExcel: boolean = false;

  maxDate: Date = new Date();
  flightReports: FlightReport[];

  columnDefs = [
    {
      headerName: "Status",
      field: "Type",
      width: 180,
      sortingOrder: ["asc", "desc"],
      cellRenderer: (result) => {
        let abb = activityAbbreviations.find(
          (x) => x.state == result.data.Type
        );
        return abb ? abb.translated : result.data.type;
      },
    },
    {
      headerName: "Tid",
      field: "started",
      width: 150,
      cellRenderer: (result) => {
        return result.data.started ? this.getTime(result.data.started) : "";
      },
      sort: { direction: "asc", priority: 0 },
      sortingOrder: ["asc", "desc"],
    },
  ];

  spinnerConfig: ISpinnerConfig;
  localeText = {};

  constructor(
    private readonly toaster: ToastrService,
    private readonly reportService: ReportService) {
    this.localeText = localeSwedish;

    this.searchForm = new UntypedFormGroup({
      startDate: new UntypedFormControl([Validators.required, DateValidation()]),
      endDate: new UntypedFormControl([Validators.required, DateValidation()]),
      flight: new UntypedFormControl(""),
    });
  }

  ngOnInit() {
    this.spinnerConfig = {
      placement: SPINNER_PLACEMENT.block_ui,
      animation: SPINNER_ANIMATIONS.horizontal_bounce,
      size: "2rem",
      color: "#7AB800",
      bgColor: "transparent",
    };

    this.flightInput.valueChanges.subscribe(() => {
      this.inputChange();
    });

    this.startDateInput.valueChanges.subscribe(() => {
      this.inputChange();
    });

    this.endDateInput.valueChanges.subscribe(() => {
      this.inputChange();
    });
  }

  canSearch() {
    return this.searchForm.valid && !this.busy;
  }

  inputChange() {
    if (this.canExport) {
      this.flightReports = null;
    }
  }

  export() {
    ExcelReportWriterHelper.exportFlightReport(
      this.flightReports,
      new Date(this.startDateInput.value),
      new Date(this.endDateInput.value)
    );
  }

  private canExport() {
    this.flightReports.forEach((report) => {
      if (report.drivers.length > 0) this.canExportToExcel = true;
    });
  }

  missingActivities(report: FlightReport) {
    if (report && report.drivers) return report.drivers.length === 0;

    return true;
  }

  async search() {
    if (this.searchForm.valid) {
      this.spinner.show();
      let startDate = new Date(this.startDateInput.value);
      let endDate = new Date(this.endDateInput.value);
      let flight = this.flightInput.value;
      this.busy = true;
      this.noMatch = false;
      this.flightReports = [];
      try {
        let response: any = await this.reportService.getflightReport(
          startDate.toDateString(),
          endDate.toDateString(),
          flight
        );
        this.spinner.hide();

        response.forEach((report) => {
          let flightReport = new FlightReport();
          flightReport.flightData = new FlightData();
          flightReport.drivers = [];

          if (report.activities) {
            flightReport.drivers = this.processActivities(report);
          }

          flightReport.flightData.isArrival = report.isArrival;
          flightReport.flightData.aircraft = report.aircraft;
          flightReport.flightData.aircraftRegistration = report.aircraftRegistration;
          flightReport.flightData.airlineOperator = report.airlineOperator;
          flightReport.flightData.blockTime = report.blockTime ? this.getTime(report.blockTime) : null;
          flightReport.flightData.diIndicator = report.diIndicator;
          flightReport.flightData.flightId = report.flightId;
          flightReport.flightData.flightServiceType = report.flightServiceType;
          flightReport.flightData.gate = report.gate;
          flightReport.flightData.isInvoiced = report.isInvoiced ? "Ja" : "Nej";
          flightReport.flightData.busIsNeeded = report.paxBusIsNeeded ? "Ja": "Nej";
          flightReport.flightData.pax = report.personsOnBoard;
          flightReport.flightData.scheduledTime = new Date(report.scheduledTime);
          flightReport.flightData.stand = report.stand;

          this.flightReports.push(flightReport);
        });

        this.canExport();

        this.busy = false;
      } catch (error) {
        this.spinner.hide();
        this.noMatch = error.request.status == 404;
        this.busy = false;
        this.flightReports = [];
        if (!this.noMatch)
          this.showError("Ett fel uppstod vid inläsning av data.");
      }
    }
  }

  getTime(date: Date) {
    return moment(date).format("HH:mm");
  }

  getDate(date: Date) {
    return moment(date).format("YYYY-MM-DD");
  }

  showError(message: string) {
    this.toaster.error(message);
  }

  processActivities(report: any): Driver[] {
    let drivers = [];
    report.activities.forEach((act: any) => {
      let activity = act;
      activity.direction = report.isArrival ? "A" : "D";
      let driver: Driver = drivers.find((x) => x.name === act.driver);
      if (driver) {
        driver.activities.push(act);
      } else {
        let newDriver = new Driver();
        newDriver.name = act.driver;
        newDriver.activities.push(act);
        drivers.push(newDriver);
      }
    });

    return drivers;
  }

  get startDateInput() {
    return this.searchForm.get("startDate");
  }
  get endDateInput() {
    return this.searchForm.get("endDate");
  }
  get flightInput() {
    return this.searchForm.get("flight");
  }
}
